import {
  type ColumnDefinition,
  exists,
  type Group,
  type GroupAndAll,
} from '~/utils'

/**
 * グループ（表頭の列）が「何で構成されているか」を表す人間可読な文字列のマップ。
 *
 * 生成元は画面ごとに異なる（カスタマイズ分析はブランド×9segs、
 * オーバーラップ分析はブランド1×ブランド2の5segs組み合わせ）ため、
 * 文字列化は各ページ側で行い、ここには出来上がった文字列だけが渡ってくる。
 * 定義を持たないグループは欠落してよい。
 */
export type GroupDefinitionMap = Partial<Record<Group, string>>

type BuildGroupColumnDefinitionsParams = {
  groupAndAllNameMap: Record<GroupAndAll, string>
  groupDefinitionMap: GroupDefinitionMap
  groups: Group[]
}

/**
 * AIコメント生成に添える「列の定義」を、実際に表示されている列の順に組み立てます。
 *
 * ラベルは表頭に描画されているグループ名そのもの（ユーザーがリネームしていればその名前）を使うため、
 * LLM 側で表の列と定義行が同じキーで突き合わせられます。
 * 「全体」列と差分列は定義を持たないので対象外です（差分列名が `A vs B` 形式で意味を自己完結して持っている）。
 *
 * @param params - 組み立てに必要なパラメーター。
 * @param params.groupAndAllNameMap - 表頭に描画されているグループ名のマップ。
 * @param params.groupDefinitionMap - グループ → 定義文字列のマップ。
 * @param params.groups - 集計済みで表に出ているグループ。
 * @returns 定義を持つグループだけの ColumnDefinition 配列。
 */
export const buildGroupColumnDefinitions = ({
  groupAndAllNameMap,
  groupDefinitionMap,
  groups,
}: BuildGroupColumnDefinitionsParams): ColumnDefinition[] =>
  groups.flatMap((group) => {
    const definition = groupDefinitionMap[group]
    if (!exists(definition) || definition === '') {
      return []
    }

    return [{ definition, label: groupAndAllNameMap[group] }]
  })
